/// <amd-module name="Search/Shared/Base/Stores/CompositeStoresManager" />
import { Action } from "VSS/Reflux/Action";
import { Store } from "VSS/Reflux/Store";

/**
 * Porting these changes from VersionControl/Scenarios/Explorer/Stores/StoresHub.ts
 */

export type EmitChangedFunction<TPayload> = (changedStores: string[], action: Action<TPayload>, payload: TPayload) => void;

export class CompositeStore extends Store {
    constructor(public readonly childStores: string[]) {
        super("SearchCompositeStoreChangedEvent");
    }

    public emitCompositeChanged(): void {
        this.emitChanged();
    }
}

interface IActionListener<TPayload> {
    action: Action<TPayload>;

    stores: string[];

    handles: ((payload: TPayload) => void)[];
}

export class ListeningActionsManager {
    private listeners: IActionListener<any>[] = [];

    constructor(private readonly emitChanged: EmitChangedFunction<any>) { }

    public listen<TPayload>(action: Action<TPayload>, storeName: string, handle: (payload: TPayload) => void): void {
        const listener: IActionListener<TPayload> = this.getOrCreate(action);

        if (listener.stores.indexOf(storeName) >= 0) {
            throw new Error("A store must only listen once each action.");
        }

        listener.stores.push(storeName);

        listener.handles.push(handle);
    }

    public dispose = (): void => {
        this.listeners.forEach(listener => {
            listener.handles = [];
            listener.stores = [];
        });
        this.listeners = [];
    };

    private getOrCreate<TPayload>(action: Action<TPayload>): IActionListener<TPayload> {
        const filtered: IActionListener<TPayload>[] = this.listeners.filter(existing => existing.action === action);
        if (filtered.length) {
            return filtered[0];
        } else {
            const actionListener: IActionListener<TPayload> = { action, stores: [], handles: [] };

            action.addListener(payload => {
                for (let i: number = 0; i < actionListener.handles.length; i++) {
                    const handle: (payload: TPayload) => void = actionListener.handles[i];
                    handle(payload);
                }

                this.emitChanged(actionListener.stores, action, payload);
            });

            this.listeners.push(actionListener);

            return actionListener;
        }
    }
}

export class CompositeStoresManager {
    private compositeStores: CompositeStore[] = [];

    public getOrCreate(storeNames: string[]): CompositeStore {
        let store: CompositeStore = this.compositeStores.filter(s => areEqualSets(s.childStores, storeNames))[0];

        if (!store) {
            store = new CompositeStore(storeNames);
            this.compositeStores.push(store);
        }

        return store;
    }

    public dispose = (): void => {
        this.compositeStores = [];
    };

    public emitCompositeChanged = (changedStores: string[]): void => {
        for (const store of this.compositeStores) {
            if (intersect(changedStores, store.childStores).length) {
                store.emitCompositeChanged();
            }
        }
    };
}

function areEqualSets<T>(a: T[], b: T[]): boolean {
    return a.length === b.length && intersect(a, b).length === a.length;
}

function intersect<T>(a: T[], b: T[]): T[] {
    return a.filter(item => b.indexOf(item) >= 0);
}
